import { Briefcase, Building, ChevronLeftIcon, Clock, Mail, MapPin, Phone, User } from "lucide-react";
import { ContainerCard, EmployeeDetails } from "../../components/ui/cards";
import { useLocation, useNavigate } from "react-router-dom";
import useApiMethods from "../../lib/useApiMethods";
import ToastMsg from "../../components/common/ToastMsg";
import { useEffect, useState } from "react";
import { SUBORDINATE_ATTENDANCE_SUMMARY_URL, SUBORDINATES_URL } from "../../constants/api";
import { Reactable } from "../../components/ui/reactable";
import dayjs from "dayjs";
import EmployeeAllowanceContent from "./helpers/EmployeeAllowanceContent";
import EmployeeDeductionContent from "./helpers/EmployeeDeductionContent";
import EmployeeDepositContent from "./helpers/EmployeeDepositContent";

const SubordinateViewDetails = () => {

    let navigate = useNavigate();
    let location = useLocation();
    let { employeeID, subordinateId } = location?.state || {};

    const { fetch } = useApiMethods()
    const [data, setData] = useState(null)
    const [loading, setLoading] = useState(false);
    const [attendanceSummary, setAttendanceSummary] = useState(null);
    const [fetchFilter, setfetchFilter] = useState()
    const [totalRecords, setTotalRecords] = useState(0)

    const getSubordinateDetailsById = async (id) => {
        try {
            setLoading(true);
            const response = await fetch(SUBORDINATES_URL, { subordinate_id: id });

            if (response.status === 'success') {
                let data = response.data[0];
                setData(data);
            }

        } catch (error) {
            setLoading(false);
            ToastMsg('error', error.message)
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        getSubordinateDetailsById(subordinateId);
    }, [])

    const dayWiseAttendanceColumns = [
        {
            accessor: "date",
            Header: "Date",
            filterable: true,
            Cell: ({ value }) => value ?? "-",
        },
        {
            accessor: "shift",
            Header: "Shift",
            filterable: true,
            Cell: ({ value }) => value ?? "-",
        },
        {
            accessor: "check_in",
            Header: "Check In",
            Cell: ({ value }) =>
                value && dayjs(value).isValid()
                    ? dayjs(value).format("hh:mm A")
                    : "-",
        },
        {
            accessor: "check_out",
            Header: "Check Out",
            Cell: ({ value }) =>
                value && dayjs(value).isValid()
                    ? dayjs(value).format("hh:mm A")
                    : "-",
        },
        {
            accessor: "overtime",
            Header: "Overtime",
            Cell: ({ value }) => value ?? "-",
        },
        {
            accessor: "break_hrs",
            Header: "Break Hours",
            Cell: ({ value }) => value ?? "-",
        },
        {
            accessor: "total_working_hours",
            Header: "Total Hours",
            Cell: ({ value }) => value ?? "-",
        },
        {
            accessor: "location",
            Header: "Location",
            Cell: ({ value }) => value ?? "-",
        },
    ];

    // Helper function
    const getDefaultAvatar = (name) => {
        // Get first letter and make uppercase
        const firstLetter = name ? name.charAt(0).toUpperCase() : 'U';

        // Return UI Avatars URL with the first letter
        return `https://ui-avatars.com/api/?name=${firstLetter}&background=04b9b3f1&color=fff&size=128`;
    };

    return (
        <ContainerCard scrollable>
            <div className="flex justify-between items-center mb-4">
                <span className=" flex gap-2 items-center">
                    <ChevronLeftIcon onClick={() => { navigate(-1) }} className=" cursor-pointer" />
                    <h1 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                        Subordinate Details
                    </h1>
                </span>
            </div>

            {loading ? (
                <div className="flex items-center justify-center h-32">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sidebar-primary"></div>
                </div>
            ) : (
                <>
                    {data && (
                        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm p-6 mb-6">
                            <div className="flex items-start gap-6 mb-3">
                                <div className="flex-shrink-0">
                                    <img
                                        src={data.profile || getDefaultAvatar(data.employee_name)}
                                        alt="Profile"
                                        className="w-10 h-10 rounded-full object-cover border-2 border-gray-200 dark:border-gray-600"
                                    />
                                </div>
                                <div className="flex-1">
                                    <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">
                                        {data.employee_name}
                                    </h2>
                                    <div className=" flex gap-3 items-center">
                                        <p className="text-sm text-gray-600 dark:text-gray-400  border-r-2 pr-2">
                                            {data.designation}
                                        </p>
                                        <p className="text-sm text-gray-500 dark:text-gray-500">
                                            Employee ID: {data.employee_id}
                                        </p>
                                    </div>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                <div className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                                    <Mail className="w-5 h-5 text-gray-500" />
                                    <div>
                                        <p className="text-sm text-gray-500 dark:text-gray-400">Email</p>
                                        <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{data.email}</p>
                                    </div>
                                </div>

                                <div className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                                    <Phone className="w-5 h-5 text-gray-500" />
                                    <div>
                                        <p className="text-sm text-gray-500 dark:text-gray-400">Phone</p>
                                        <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{data.phone_number}</p>
                                    </div>
                                </div>

                                <div className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                                    <MapPin className="w-5 h-5 text-gray-500" />
                                    <div>
                                        <p className="text-sm text-gray-500 dark:text-gray-400">Location</p>
                                        <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{data.location}</p>
                                    </div>
                                </div>

                                <div className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                                    <Building className="w-5 h-5 text-gray-500" />
                                    <div>
                                        <p className="text-sm text-gray-500 dark:text-gray-400">Department</p>
                                        <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{data.department}</p>
                                    </div>
                                </div>

                                <div className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                                    <Briefcase className="w-5 h-5 text-gray-500" />
                                    <div>
                                        <p className="text-sm text-gray-500 dark:text-gray-400">Employee Type</p>
                                        <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{data.employee_type}</p>
                                    </div>
                                </div>

                                <div className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                                    <Clock className="w-5 h-5 text-gray-500" />
                                    <div>
                                        <p className="text-sm text-gray-500 dark:text-gray-400">Shift</p>
                                        <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{data.shift}</p>
                                    </div>
                                </div>

                                <div className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                                    <User className="w-5 h-5 text-gray-500" />
                                    <div>
                                        <p className="text-sm text-gray-500 dark:text-gray-400">Reporting Manager</p>
                                        <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{data.reporting_manager}</p>
                                    </div>
                                </div>

                                {data.work_location && (
                                    <div className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                                        <MapPin className="w-5 h-5 text-gray-500" />
                                        <div>
                                            <p className="text-sm text-gray-500 dark:text-gray-400">Work Location</p>
                                            <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{data.work_location}</p>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {subordinateId && (
                        <div className="mb-6">
                            <EmployeeAllowanceContent employeeID={subordinateId} />
                        </div>
                    )}

                    {subordinateId && (
                        <div className="mb-6">
                            <EmployeeDeductionContent employeeID={subordinateId} />
                        </div>
                    )}

                    {subordinateId && (
                        <div className="mb-6">
                            <EmployeeDepositContent employeeID={subordinateId} />
                        </div>
                    )}

                    <div className="mb-4 pb-6">
                        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3">Attendance Summary</h2>
                        <Reactable
                            fillHeight={false}
                            columns={dayWiseAttendanceColumns}
                            data={attendanceSummary}
                            setData={setAttendanceSummary}
                            setTotalCount={setTotalRecords}
                            url={SUBORDINATE_ATTENDANCE_SUMMARY_URL}
                            serverSide={true}
                            rowSize="md"
                            isCSVRequired={true}
                            isPDFRequired={true}
                            isMonthYearRequired={true}
                            filter={{ employee_id: employeeID }}
                            printFilter={{ employee_id: employeeID }}
                            manualPagination={true}
                        />
                    </div>
                </>
            )}
        </ContainerCard>
    );
}

export default SubordinateViewDetails;